import type { DriveStep } from 'driver.js';

export type WalkthroughTFunction = (
  key: string,
  options?: Record<string, unknown>
) => string;

export type WalkthroughStepKey = 'energy-zones' | 'add-task' | 'drag-to-calendar' | 'ai-chat';

export interface WalkthroughStep extends DriveStep {
  key: WalkthroughStepKey;
}

export const WALKTHROUGH_STEPS: WalkthroughStep[] = [
  {
    key: 'energy-zones',
    element: '#calendar-view',
    popover: {
      side: 'left',
      align: 'center',
    },
  },
  {
    key: 'add-task',
    element: '[data-testid="add-task-button"]',
    popover: {
      side: 'top',
      align: 'start',
    },
  },
  {
    key: 'drag-to-calendar',
    element: '.kanban-task-card',
    popover: {
      side: 'top',
      align: 'start',
    },
  },
  {
    key: 'ai-chat',
    element: '[data-testid="ai-chat-input"]',
    popover: {
      side: 'top',
      align: 'center',
    },
  },
];

export const WALKTHROUGH_TOTAL_STEPS = WALKTHROUGH_STEPS.length;

export function getWalkthroughAiPrefill(t: WalkthroughTFunction): string {
  return t('walkthrough.aiPrefill');
}

export function buildProgressTitle(t: WalkthroughTFunction, stepIndex: number, title: string): string {
  const dots = Array.from({ length: WALKTHROUGH_TOTAL_STEPS }, (_, i) =>
    `<span style="display:inline-block;width:6px;height:6px;border-radius:50%;background:${i === stepIndex ? '#680ddb' : '#e0e0e0'};margin:0 2px;vertical-align:middle;"></span>`
  ).join('');
  const progress = t('walkthrough.progress', {
    current: stepIndex + 1,
    total: WALKTHROUGH_TOTAL_STEPS,
  });

  return `<span style="display:flex;align-items:center;gap:6px;margin-bottom:6px;">${dots}<span style="font-size:11px;color:#999;font-weight:400;">${progress}</span></span>${title}`;
}

export function buildWalkthroughSteps(
  t: WalkthroughTFunction,
  options: { isMobile?: boolean } = {}
): DriveStep[] {
  return WALKTHROUGH_STEPS.map((step, index) => {
    const title = t(`walkthrough.steps.${step.key}.title`);
    const description =
      index === 2 && options.isMobile
        ? t('walkthrough.steps.drag-to-calendar.descriptionMobile')
        : t(`walkthrough.steps.${step.key}.description`);

    return {
      ...step,
      popover: {
        ...step.popover,
        title: buildProgressTitle(t, index, title),
        description,
        showButtons: (index === 0
          ? ['next']
          : index === 2
          ? []
          : ['next', 'previous']) as ('next' | 'previous' | 'close')[],
        nextBtnText:
          index === WALKTHROUGH_TOTAL_STEPS - 1
            ? t('walkthrough.buttons.done')
            : t('walkthrough.buttons.next'),
        prevBtnText: t('walkthrough.buttons.back'),
      },
    };
  });
}
